import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { ITask, TASK_STATUS_LABELS, TaskStatus } from '@/types/task';

export interface PhaseTaskNodeData {
  task: ITask;
  onEdit: (task: ITask) => void;
  onAddPrior: (task: ITask) => void;
  onAddPost: (task: ITask) => void;
}

interface PhaseTaskNodeProps {
  data: PhaseTaskNodeData;
}

const statusColors: Record<TaskStatus, { bg: string; border: string; text: string; badge: string }> = {
  plan: {
    bg: 'bg-gray-50 dark:bg-gray-800',
    border: 'border-gray-300 dark:border-gray-600',
    text: 'text-gray-700 dark:text-gray-300',
    badge: 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
  },
  doing: {
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    border: 'border-blue-400 dark:border-blue-500',
    text: 'text-blue-700 dark:text-blue-300',
    badge: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
  },
  review: {
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    border: 'border-amber-400 dark:border-amber-500',
    text: 'text-amber-700 dark:text-amber-300',
    badge: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300',
  },
  done: {
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
    border: 'border-emerald-400 dark:border-emerald-500',
    text: 'text-emerald-700 dark:text-emerald-300',
    badge: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300',
  },
};

const priorityColors: Record<string, string> = {
  low: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
  medium: 'bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400',
  high: 'bg-orange-100 text-orange-600 dark:bg-orange-900/40 dark:text-orange-400',
  critical: 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400',
};

const PhaseTaskNode = memo(({ data }: PhaseTaskNodeProps) => {
  const { task, onEdit, onAddPrior, onAddPost } = data;
  const colors = statusColors[task.status] || statusColors.plan;

  const handleNodeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit(task);
  };

  const handleAddPrior = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAddPrior(task);
  };

  const handleAddPost = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAddPost(task);
  };

  return (
    <div className="relative group">
      {/* Add Prior Button (left side) */}
      <button
        onClick={handleAddPrior}
        className="absolute -left-8 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md z-10"
        title="Add prior dependency task"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      </button>

      {/* Node Content */}
      <div
        onClick={handleNodeClick}
        className={`px-4 py-3 rounded-lg border-2 shadow-md cursor-pointer min-w-[180px] max-w-[220px] transition-all hover:shadow-lg hover:scale-[1.02] ${colors.bg} ${colors.border}`}
      >
        {/* Input Handle */}
        <Handle
          type="target"
          position={Position.Left}
          className="w-3 h-3 !bg-gray-400 dark:!bg-gray-500 !border-2 !border-white dark:!border-gray-800"
        />

        {/* Header: Status Badge + Duration */}
        <div className="flex items-center justify-between mb-2">
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${colors.badge}`}>
            {TASK_STATUS_LABELS[task.status]}
          </span>
          {task.duration > 0 && (
            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
              {task.duration}h
            </span>
          )}
        </div>

        {/* Task Name */}
        <h4 className={`font-medium text-sm mb-2 line-clamp-2 ${colors.text}`}>
          {task.task_name}
        </h4>

        {/* Priority + Owner Row */}
        <div className="flex items-center justify-between gap-2">
          <span className={`text-xs px-1.5 py-0.5 rounded ${priorityColors[task.priority] || priorityColors.medium}`}>
            {task.priority}
          </span>
          <div className="flex items-center gap-1.5 min-w-0">
            <div className="w-5 h-5 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-medium text-orange-600 dark:text-orange-400">
                {task.task_owner?.charAt(0).toUpperCase() || '?'}
              </span>
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {task.task_owner}
            </span>
          </div>
        </div>

        {/* Date info */}
        {task.planned_end && (
          <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
            <span className="text-xs text-gray-400 dark:text-gray-500">
              Due: {new Date(task.planned_end).toLocaleDateString()}
            </span>
          </div>
        )}

        {/* Output Handle */}
        <Handle
          type="source"
          position={Position.Right}
          className="w-3 h-3 !bg-gray-400 dark:!bg-gray-500 !border-2 !border-white dark:!border-gray-800"
        />
      </div>

      {/* Add Post Button (right side) */}
      <button
        onClick={handleAddPost}
        className="absolute -right-8 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md z-10"
        title="Add post dependency task"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      </button>
    </div>
  );
});

PhaseTaskNode.displayName = 'PhaseTaskNode';

export default PhaseTaskNode;
